import { cardinal } from "./friends"
import { CLIENT_CONTRACT } from "./client_contract"
import { metersBetweenLatLon } from "./geo_math"
import { WEB_LOCATION_POLICY, metersToFeet, metersToMiles, speedMphLabel } from "./web_location_policy"

export const TRIP_PROFILE_UNITS = Object.freeze({
  metersToMiles: WEB_LOCATION_POLICY.metersToMiles,
  metersToFeet: WEB_LOCATION_POLICY.metersToFeet,
  metersPerSecondToMilesPerHour: WEB_LOCATION_POLICY.metersPerSecondToMilesPerHour
})

// The trip page draws one SVG path per segment and embeds every point in the
// HTML, so the working set must stay small enough for the browser to render.
// The server pre-samples to TripPolicy::TRACK_PROFILE_POINT_LIMIT; this is the
// client-side guard for anything that still arrives oversized.
export const TRIP_PROFILE_POINT_LIMIT = CLIENT_CONTRACT.server.trip.trackProfilePointLimit

// Stride-sample a trip's points down to `limit`, always keeping the first and
// last point so the route's endpoints (and its Start/End markers) survive.
// Small sets pass through untouched.
export function sampleTripPoints(points, limit = TRIP_PROFILE_POINT_LIMIT) {
  const list = Array.isArray(points) ? points : Array.from(points || [])
  const cap = Math.max(2, Math.floor(Number(limit)) || TRIP_PROFILE_POINT_LIMIT)
  if (list.length <= cap) return list
  const stride = Math.ceil(list.length / cap)
  const sampled = list.filter((_, index) => index % stride === 0)
  const lastPoint = list[list.length - 1]
  if (sampled[sampled.length - 1] !== lastPoint) sampled.push(lastPoint)
  return sampled
}

export function bearingBetweenPoints(from, to) {
  if (!validPoint(from) || !validPoint(to)) return null
  const rad = Math.PI / 180
  const y = Math.sin((to.lon - from.lon) * rad) * Math.cos(to.lat * rad)
  const x = Math.cos(from.lat * rad) * Math.sin(to.lat * rad) -
    Math.sin(from.lat * rad) * Math.cos(to.lat * rad) * Math.cos((to.lon - from.lon) * rad)
  return ((Math.atan2(y, x) * 180 / Math.PI) + 360) % 360
}

export function tripPointReadout(points, index) {
  const point = points[index] || {}
  const previous = index > 0 ? points[index - 1] : point
  const next = index > 0 ? point : points[index + 1]
  const bearing = previous && next && previous !== next ? bearingBetweenPoints(previous, next) : null
  const speedLabel = speedMphLabel(point.speed)
  return {
    elevation: point.ele != null ? `${Math.round(metersToFeet(point.ele)).toLocaleString()} ft` : "-",
    speed: speedLabel === "–" ? "-" : `${speedLabel} mph`,
    heading: bearing == null ? "-" : cardinal(bearing)
  }
}

export function elevationHue(elevation, min, max) {
  if (elevation == null) return null
  const span = (max - min) || 1
  return 120 - 120 * Math.max(0, Math.min(1, (elevation - min) / span))
}

export function elevationSegments(points) {
  const elevations = points.map(point => point.ele).filter(elevation => elevation != null)
  if (!elevations.length) return []
  const min = Math.min(...elevations)
  const max = Math.max(...elevations)
  const segments = []
  for (let i = 1; i < points.length; i += 1) {
    const elevation = points[i].ele ?? points[i - 1].ele
    segments.push({
      from: [points[i - 1].lat, points[i - 1].lon],
      to: [points[i].lat, points[i].lon],
      color: elevation != null ? `hsl(${elevationHue(elevation, min, max)},80%,45%)` : "#2563eb"
    })
  }
  return segments
}

export function elevationChartSeries(points) {
  const elevated = points.filter(point => point.ele != null)
  let distanceM = 0
  let last = null
  const labels = []
  const data = []
  const coords = []

  for (const point of elevated) {
    if (last) distanceM += metersBetweenLatLon(last, point)
    last = point
    labels.push(metersToMiles(distanceM).toFixed(2))
    data.push(Math.round(metersToFeet(point.ele)))
    coords.push([point.lat, point.lon])
  }

  return { labels, data, coords }
}

function validPoint(point) {
  return point?.lat != null && point?.lon != null &&
    Number.isFinite(Number(point.lat)) && Number.isFinite(Number(point.lon))
}
